import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const DashboardCharts = ({ employees, attendanceRecords }) => {
  // Calculate chart data (last 7 days attendance)
  const last7Days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    last7Days.push(d.toISOString().split('T')[0]);
  }

  const chartData = last7Days.map(date => {
    const dayRecords = attendanceRecords.filter(r => r.date === date);
    const entradas = dayRecords.filter(r => r.type === 'entrada');
    let tardanzas = 0;
    
    entradas.forEach(ent => {
      const emp = employees.find(e => e.id === ent.employeeId);
      if (emp) {
        const expected = emp.scheduleStart || '08:00';
        if (ent.time > expected + ':59') {
          tardanzas++;
        }
      }
    });

    return {
      name: date.slice(5), // MM-DD
      asistencias: entradas.length,
      tardanzas: tardanzas
    };
  });

  // Calculate top punctual employees
  const employeeStats = employees.map(emp => {
    const empRecords = attendanceRecords.filter(r => r.employeeId === emp.id && r.type === 'entrada');
    let onTime = 0;
    empRecords.forEach(ent => {
      const expected = emp.scheduleStart || '08:00';
      if (ent.time <= expected + ':59') {
        onTime++;
      }
    });
    return { name: emp.nombre + ' ' + emp.apellidos, onTime };
  });

  const topPunctual = employeeStats
    .sort((a, b) => b.onTime - a.onTime)
    .slice(0, 5)
    .filter(e => e.onTime > 0);

  return (
    <div className="animate-fade-in-up space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glass-card glass-card-hover glow-cyan p-6 rounded-2xl flex items-center justify-between border-slate-800/80">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-cyan-400 mb-1">
              Colaboradores Registrados
            </h3>
            <p className="text-4xl font-extrabold text-slate-100 font-mono">{employees.length}</p>
          </div>
          <div className="p-3 bg-cyan-500/10 rounded-xl text-cyan-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 005.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        </div>

        <div className="glass-card glass-card-hover glow-green p-6 rounded-2xl flex items-center justify-between border-slate-800/80">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-1">
              Asistencias de Hoy
            </h3>
            <p className="text-4xl font-extrabold text-slate-100 font-mono">
              {attendanceRecords.filter(r => r.date === new Date().toISOString().split('T')[0] && r.type==='entrada').length}
            </p>
          </div>
          <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
        </div>

        <div className="glass-card glass-card-hover glow-purple p-6 rounded-2xl flex items-center justify-between border-slate-800/80">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-purple-400 mb-1">
              Registros Históricos
            </h3>
            <p className="text-4xl font-extrabold text-slate-100 font-mono">{attendanceRecords.length}</p>
          </div>
          <div className="p-3 bg-purple-500/10 rounded-xl text-purple-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="glass-card p-6 rounded-2xl border-slate-800/80 h-96 lg:col-span-2">
          <h3 className="text-lg font-bold text-slate-100 mb-4 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-400" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
            Tendencia de Asistencia y Tardanzas (Últimos 7 días)
          </h3>
          <ResponsiveContainer width="100%" height="90%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '0.75rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)' }}
                itemStyle={{ color: '#f1f5f9' }}
                cursor={{ fill: '#1e293b', opacity: 0.4 }}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} />
              <Bar dataKey="asistencias" name="Asistencias Puntuales" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={50} />
              <Bar dataKey="tardanzas" name="Tardanzas" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={50} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="glass-card p-6 rounded-2xl border-slate-800/80 h-96 overflow-y-auto">
          <h3 className="text-lg font-bold text-slate-100 mb-4 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
            </svg>
            Top 5 Puntualidad Histórica
          </h3>
          <div className="space-y-4 mt-6">
            {topPunctual.length > 0 ? topPunctual.map((emp, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-slate-900/50 border border-slate-800/50">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    idx === 0 ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                    idx === 1 ? 'bg-slate-300/20 text-slate-300 border border-slate-300/30' :
                    idx === 2 ? 'bg-orange-700/20 text-orange-400 border border-orange-700/30' :
                    'bg-slate-800 text-slate-400'
                  }`}>
                    {idx + 1}
                  </div>
                  <span className="text-sm font-medium text-slate-200 truncate max-w-[120px]">{emp.name}</span>
                </div>
                <div className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-md">
                  {emp.onTime} pts
                </div>
              </div>
            )) : (
              <p className="text-sm text-slate-500 text-center mt-10">Sin datos suficientes</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCharts;
